import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useCrearCaso, useInformesDelLaboratorio } from "@/hooks/useLaboratorio";
import { getApiErrorMessage } from "@/lib/api";

export interface OrigenDelCaso {
  /** La corrida de la que se congela; sin ella, la persona elige un informe. */
  corridaId: number | null;
  nombreSugerido?: string;
}

/**
 * Congela un informe como caso de prueba: desde el informe mismo o desde una
 * corrida suya (el caso lleva entonces la referencia a la corrida). El servidor
 * toma la foto; acá solo se le pone nombre y, si se quiere, un tipo libre.
 */
export function NuevoCasoDialog({
  origen,
  onCerrar,
  onCreado,
}: {
  /** `null` cierra el diálogo. */
  origen: OrigenDelCaso | null;
  onCerrar: () => void;
  onCreado?: () => void;
}) {
  if (!origen) return null;

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-md">
        <Formulario origen={origen} onCerrar={onCerrar} onCreado={onCreado} />
      </DialogContent>
    </Dialog>
  );
}

function Formulario({ origen, onCerrar, onCreado }: { origen: OrigenDelCaso; onCerrar: () => void; onCreado?: () => void }) {
  const informes = useInformesDelLaboratorio();
  const crear = useCrearCaso();

  const [informeId, setInformeId] = useState("");
  const [nombre, setNombre] = useState(origen.nombreSugerido ?? "");
  const [etiqueta, setEtiqueta] = useState("");

  const desdeCorrida = origen.corridaId !== null;
  const completo = nombre.trim() !== "" && (desdeCorrida || informeId !== "");

  function elegirInforme(id: string) {
    setInformeId(id);
    // El nombre se sugiere con la propiedad solo mientras nadie lo haya escrito.
    if (nombre.trim() === "") {
      const informe = informes.data?.find((i) => String(i.id) === id);
      if (informe?.propiedad_nombre) setNombre(informe.propiedad_nombre);
    }
  }

  function enviar() {
    crear.mutate(
      {
        nombre: nombre.trim(),
        etiqueta: etiqueta.trim() === "" ? null : etiqueta.trim(),
        ...(desdeCorrida ? { corrida_id: origen.corridaId! } : { informe_id: Number(informeId) }),
      },
      {
        onSuccess: () => {
          toast.success("Caso congelado.");
          onCreado?.();
          onCerrar();
        },
        onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos congelar el caso.")),
      },
    );
  }

  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (completo && !crear.isPending) enviar();
      }}
    >
      <DialogHeader>
        <DialogTitle>{desdeCorrida ? "Nuevo caso desde una corrida" : "Nuevo caso"}</DialogTitle>
        <DialogDescription>
          Se guarda una foto del informe tal como está hoy: no cambia aunque el informe siga avanzando.
        </DialogDescription>
      </DialogHeader>

      {desdeCorrida ? null : (
        <div className="grid gap-1.5">
          <Label htmlFor="caso-informe">Informe</Label>
          <NativeSelect id="caso-informe" value={informeId} onChange={(e) => elegirInforme(e.target.value)}>
            <option value="">Elige un informe</option>
            {(informes.data ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {[i.numero_informe, i.propiedad_nombre].filter(Boolean).join(" · ")}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}

      <div className="grid gap-1.5">
        <Label htmlFor="caso-nombre">Nombre</Label>
        <Input id="caso-nombre" value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="caso-etiqueta">Tipo (opcional)</Label>
        <Input
          id="caso-etiqueta"
          value={etiqueta}
          maxLength={40}
          placeholder="Frutícola, Secano, Muchos roles…"
          onChange={(e) => setEtiqueta(e.target.value)}
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!completo || crear.isPending}>
          Congelar caso
        </Button>
      </DialogFooter>
    </form>
  );
}
